'use client';
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable tailwindcss/no-custom-classname */
import classNames from 'classnames';
import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { useBreakpoint } from '../../utils/useBreakpoint';
import Button from '../Button';
import { CollapsibleSection } from '../CollapsibleSection/CollapsibleSection';
import { SlideUpModal } from '../SlideUpModal';
import { Bullets } from './components/Bullet';
import { TRANSLATIONS } from './i18n';
import { OddsConsentModalProps } from './types';

export const OddsConsentModal = ({
  slideUpModalRef,
  onClose,
  onClickAgree,
  agreement,
  pushDataLayer,
}: OddsConsentModalProps) => {
  const [checkAgree, setCheckAgree] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isVisible, toggleVisibility] = useState(false);
  const activeBreakpoint = useBreakpoint({
    _: '_',
    sm: 'sm',
    md: 'md',
    lg: 'lg',
    xl: 'xl',
    xxl: 'xxl',
  });
  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  const handleOnClose = () => {
      pushDataLayer?.({
        event:'form_step',
        lead_form_name:'pl-chc-approval-odds-consent-form',
        form_type:'consent form',
        action:'back',
        component_type:'footer',
        component_title:'CHC Approval Odds Consent Drawer/Footer CTAs_Not Now',
        component_index:'',
        vertical_code:'PL',
        form_field_value:'',
        step_number:'1',
        step_name:'consent',
        status:'',
    })
    onClose?.();
    slideUpModalRef.current?.close();
  };

  const handleClickConfirm = async () => {
    if (isConfirming) {
      return;
    }

    setIsConfirming(true);
    try {
      await onClickAgree?.();
      pushDataLayer?.({
        event:'form_step',
        lead_form_name:'pl-chc-approval-odds-consent-form',
        form_type:'consent form',
        action:'submit',
        component_type:'footer',
        component_title:'CHC Approval Odds Consent Drawer/Footer CTAs_Proceed',
        component_index:'1',
        vertical_code:'PL',
        form_field_value:'true',
        step_number:'1',
        step_name:'consent',
        status:''
    })
    } finally {
      setIsConfirming(false);
    }
  };

  const components = {
    div: ({ className, children, ...props }: any) => {
      if (className === 'subtitle') {
        return (
          <div
            className="px-4 font-font-weight/body-2 text-font-size/body-2 leading-line-height/body-2-relaxed lg:px-6"
            {...props}
          >
            {children}
          </div>
        );
      }

      if (className === 'bullets') {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const extractBullets = (_node: any) => {
          const texts: string[] = [];
          const extract = (node: any) => {
            if (typeof node === 'string') {
              texts.push(node);
            } else if (Array.isArray(node)) {
              node.forEach(extract);
            } else if (React.isValidElement(node)) {
              // @ts-expect-error - react-markdown v10 passes props with unknown structure, children exists at runtime
              if (node.props?.children) extract(node.props.children);
            }
          };
          extract(children);

          const fullText = texts.join('');
          return fullText
            .split('\n')
            .filter((line) => line.trim().startsWith('-'))
            .map((line) => line.replace(/^-\s*/, '').trim());
        };

        const bullets = extractBullets(children);
        return <Bullets bullets={bullets} className="px-4 lg:px-6" />;
      }

      if (className === 'checkbox') {
        return (
          <div
            className="relative z-10 flex items-center gap-2 border-t p-spacing/gap/section-y pb-2"
            {...props}
          >
            <div className="flex flex-row gap-2">
              <input
                type="checkbox"
                className="size-form/check-radio-size"
                checked={checkAgree}
                onChange={() => {
                  const willBeChecked = !checkAgree;
                  pushDataLayer?.({
                    event:'form_interact',
                    lead_form_name:'pl-chc-approval-odds-consent-form',
                    form_type:'consent form',
                    action:`${willBeChecked ? 'check' : 'uncheck'}`,
                    component_type:'checkbox',
                    component_title:'CHC Approval Odds Consent Drawer/Consent Acknowledgement_Credit Data Usage',
                    component_index:'',
                    vertical_code:'PL',
                    form_field_value:'',
                    step_number:'1',
                    step_name:'consent',
                    status:''
                  })
                  setCheckAgree(willBeChecked);
                }}
              />
              <p className="font-font-weight/body-2 text-text/primary text-font-size/body-2 leading-line-height/body-2">
                {children}
              </p>
            </div>
          </div>
        );
      }

      return <div {...props}>{children}</div>;
    },

    h2: ({ className, title, children, ...props }: any) => {
      if (className === 'accordion') {
        return (
          <div className="px-4 lg:px-6">
            <CollapsibleSection
              id="bullets"
              isOpen={isVisible}
              onToggle={() => {
                const willBeVisibled = !isVisible;
                pushDataLayer?.({
                  event:'form_interact',
                  lead_form_name:'pl-chc-approval-odds-consent-form',
                  form_type:'consent form',
                  action:`${willBeVisibled ? 'expand' : 'collapse'}`,
                  component_type:'accordion',
                  component_title:'CHC Approval Odds Consent Drawer/Accordion_How does approval odds works',
                  component_index:'',
                  vertical_code:'PL',
                  form_field_value:'',
                  step_number:'1',
                  step_name:'consent',
                  status:''
                })
                toggleVisibility(willBeVisibled)
              }}
              content={<div className="mt-2 text-text/primary">{children}</div>}
            >
              <div className="font-bold">{title}</div>
            </CollapsibleSection>
          </div>
        );
      }
      return <h2 {...props}>{children}</h2>;
    },

    input: () => null,
  };

  if (!agreement) {
    return null;
  }

  return (
    <SlideUpModal
      title={agreement.title}
      titleClassName="rounded-radius/card"
      ref={slideUpModalRef}
      titleContainerClassName="!p-[16px] border-b"
      rootClassName="!p-[0px]"
      bodyClassName={
        activeBreakpoint === 'sm' ? '!px-[0px] !pb-0' : '!px-[0px]'
      }
      hideCloseButton
    >
      <div className="flex flex-col gap-spacing/padding/x-sm">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[rehypeRaw]}
          components={components}
        >
          {agreement.content}
        </ReactMarkdown>
        <div
          className={classNames(
            'flex flex-row gap-spacing/gap/x px-spacing/padding/x py-spacing/padding/y-sm',
            {
              'shadow-[0px_0px_12px_-1px_rgba(0,0,0,0.25)]':
                activeBreakpoint === 'sm',
            },
          )}
        >
          <Button
            className="w-full px-button/padding-regular-x text-colors/neutral-gray-700"
            styleType="neutral-light-and-blue"
            variant="outline"
            onClick={handleOnClose}
          >
            {staticText.dismiss}
          </Button>

          <Button
            className="w-full px-button/padding-regular-x text-text/on-dark"
            variant="filled"
            styleType="primary"
            disabled={!checkAgree || isConfirming}
            onClick={handleClickConfirm}
          >
            {staticText.confirm}
          </Button>
        </div>
      </div>
    </SlideUpModal>
  );
};
